import React, { useEffect, useState } from "react";
import {
  Info,
  Edit2,
  Search,
  Lock,
  Star,
  CreditCard,
  TrendingUp,
  DollarSign,
  Layers,
  ChevronLeft,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { notify } from "../../../components/Common/Alerts";
import Utils from "../../../utils";
import { decryptData } from "../../../utils/crypto";
import { getValuationAnalytics } from "../../../services/Front/dashboardService";
import { createSerachFlightApi } from "../../../services/Front/flightService";
import { useSearch } from "../../../context/SearchContext";

const CardSelectionScreen = () => {
  const state = useLocation();
  const { formData } = useSearch();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState("");
  const [cardList, setCardList] = useState(null);
  const [btnLoading, setBtnLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getValuationAnalytics()
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        setCardList(decrypted);
        if (decrypted?.cards?.length > 0) {
          setSelectedId(decrypted.cards[0].card_id);
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        notify.error(Utils.getErrorMessage(err));
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const selectedCard =
    cardList?.cards?.find((c) => Number(c.card_id) === Number(selectedId)) ||
    null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedId) {
      notify.error("Please select a card");
      return;
    }

    try {
      setBtnLoading(true);
      const payload = {
        trip_type: formData?.trip_type,
        origin: formData?.origin_code,
        destination: formData?.destination_code,
        departure_date: formData?.departure_date,
        return_date: formData?.return_date,
        cabin_class: formData?.cabin_class,
        passengers: formData?.passengers,
        allow_transfer: true,
        card_id: selectedId,
      };

      const response = await createSerachFlightApi(payload);
      const decrypted = decryptData(response.data);
      notify.success("Top flight results found");
      navigate("/results", { state: decrypted.data });
    } catch (err) {
      notify.error(Utils.getErrorMessage(err));
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <div className="bg-[#F4F6F9] dark:bg-[#0B111E] min-h-screen h-full w-full overflow-y-auto text-slate-900 dark:text-slate-100 font-sans transition-colors duration-300 antialiased pb-44 lg:pb-16">
      <div className="max-w-[1440px] mx-auto px-4 py-6 sm:px-6 lg:px-12">
        {/* Navigation / Header Section */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="hidden md:flex p-2.5 bg-white dark:bg-[#151F32] rounded-xl border border-slate-200/60 dark:border-slate-800/80 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white shadow-2xs hover:shadow-xs transition-all cursor-pointer"
          >
            <ChevronLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-400 bg-clip-text text-transparent">
              Select Point Source
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              Choose an engine optimization route for your flight search.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-8 space-y-5">
            <div className="bg-white dark:bg-[#151F32] rounded-2xl p-4 sm:p-5 border border-slate-200/60 dark:border-slate-800/50 flex items-center justify-between shadow-xs backdrop-blur-md transition-all">
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0">
                  <Info size={18} />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] uppercase font-bold tracking-wider text-slate-400 dark:text-slate-500">
                    Active Route Parameters
                  </p>
                  <p className="text-sm font-bold text-slate-700 dark:text-slate-200 mt-0.5">
                    {formData
                      ? `${formData.origin_code} → ${formData.destination_code} • ${
                          formData?.departure_date
                            ? moment(formData.departure_date).format("DD MMM")
                            : "Not set"
                        } • ${
                          formData.trip_type === "roundtrip"
                            ? "Round Trip"
                            : "One Way"
                        } • ${formData.passengers} Pax • ${formData.cabin_class?.charAt(0).toUpperCase() + formData.cabin_class?.slice(1)}`
                      : "ORD → DEL • 21 Jun • One Way • 1 Passenger • Economy Class"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate("/search")}
                className="p-2.5 text-slate-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-500/10 rounded-xl transition-all ml-2 shrink-0 cursor-pointer border border-transparent hover:border-amber-200 dark:hover:border-amber-500/20 shadow-2xs"
              >
                <Edit2 size={16} />
              </button>
            </div>

            <div className="space-y-4">
              {loading
                ? [...Array(4)].map((_, i) => (
                    <div
                      key={i}
                      className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#151F32] p-5 animate-pulse"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-5 h-5 rounded-full bg-slate-200 dark:bg-slate-700" />

                        <div className="w-20 h-12 rounded-xl bg-slate-200 dark:bg-slate-700" />

                        <div className="flex-1 space-y-2">
                          <div className="h-3 w-40 bg-slate-200 dark:bg-slate-700 rounded" />
                          <div className="h-2 w-28 bg-slate-200 dark:bg-slate-700 rounded" />

                          <div className="flex gap-4 mt-3">
                            <div className="h-3 w-20 bg-slate-200 dark:bg-slate-700 rounded" />
                            <div className="h-3 w-20 bg-slate-200 dark:bg-slate-700 rounded" />
                          </div>
                        </div>

                        <div className="w-14 h-10 bg-slate-200 dark:bg-slate-700 rounded-xl" />
                      </div>
                    </div>
                  ))
                : cardList?.cards?.map((card) => {
                    const isSelected = selectedId === card.card_id;

                    const gradientClass =
                      card.bgGradient || "from-[#1E293B] to-[#0F172A]";
                    const textColorClass = card.textColor || "text-white";

                    return (
                      <div
                        key={card.card_id}
                        onClick={() => setSelectedId(card.card_id)}
                        className={`relative rounded-2xl border transition-all duration-200 cursor-pointer overflow-hidden group
                      ${
                        isSelected
                          ? "border-amber-500 bg-amber-50/20 dark:bg-amber-500/5 shadow-xs"
                          : "border-slate-200 dark:border-slate-800/80 bg-white dark:bg-[#151F32] hover:border-slate-300 dark:hover:border-slate-700 hover:shadow-xs"
                      }`}
                      >
                        <div className="p-5 sm:p-6 flex items-center gap-4 sm:gap-6">
                          {/* Selection System */}
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all duration-200
                        ${isSelected ? "border-amber-500" : "border-slate-300 dark:border-slate-600"}`}
                          >
                            <div
                              className={`w-2.5 h-2.5 rounded-full bg-amber-500 transition-transform duration-200
                          ${isSelected ? "scale-100" : "scale-0"}`}
                            />
                          </div>

                          {/* Mock Card Asset Frame */}
                          <div
                            className={`w-18 h-11 sm:w-24 sm:h-15 rounded-xl bg-gradient-to-br ${gradientClass} p-2.5 flex flex-col justify-between shadow-xs shrink-0 relative overflow-hidden transition-all duration-200 group-hover:scale-105`}
                          >
                            <div className="absolute inset-0 bg-black/5 mix-blend-overlay" />
                            <div className="w-4 h-2.5 bg-amber-300/70 dark:bg-amber-200/40 rounded-xs relative z-10" />
                            <span
                              className={`text-[8px] sm:text-[10px] font-black uppercase tracking-wider block truncate relative z-10 opacity-90 ${textColorClass}`}
                            >
                              {card.name ? card.name.split(" ")[0] : "CARD"}
                            </span>
                          </div>

                          {/* Core Analytics Metrics Body */}
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                              <h3 className="text-sm sm:text-base font-bold tracking-tight text-slate-800 dark:text-slate-200 truncate">
                                {card.name}
                              </h3>
                            </div>

                            <p className="text-xs font-medium text-slate-400 dark:text-slate-400 mt-0.5 flex items-center gap-1.5 truncate">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                              {card.reward_program_name}
                            </p>

                            <div className="flex items-center gap-6 mt-3">
                              <div>
                                <span className="block text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                                  Points Balance
                                </span>
                                <p className="text-sm font-extrabold text-slate-800 dark:text-amber-400 mt-0.5">
                                  {Number(
                                    card.latest_points || card.points || 0,
                                  ).toLocaleString()}{" "}
                                  <span className="text-[10px] font-medium text-slate-400">
                                    pts
                                  </span>
                                </p>
                              </div>
                              <div className="w-px h-6 bg-slate-200 dark:bg-slate-700" />
                              <div>
                                <span className="block text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                                  Est. Value
                                </span>
                                <p className="text-sm font-extrabold text-emerald-600 dark:text-emerald-400 mt-0.5">
                                  ${card.latest_min || "0.00"} – $
                                  {card.latest_max || "0.00"}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Custom Metric Floating Container Block */}
                          <div className="bg-slate-50 dark:bg-slate-800/80 text-slate-800 dark:text-slate-200 text-xs font-bold px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 whitespace-nowrap ml-1 flex flex-col items-center justify-center min-w-[54px]">
                            <span className="text-[8px] uppercase tracking-wider font-semibold text-slate-400 mb-0.5">
                              CPP
                            </span>
                            <span className="text-amber-600 dark:text-amber-400 font-black text-sm sm:text-base">
                              {Number(
                                card.reward_cpp || card.reward_cpp_value || 0,
                              ).toFixed(1)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
            </div>
          </div>

          {/* Right Column Summary Widget */}
          <div className="hidden lg:block lg:col-span-4 bg-white dark:bg-[#151F32] rounded-2xl p-6 border border-slate-200/70 dark:border-slate-800/80 shadow-sm sticky top-6">
            {selectedCard ? (
              <>
                <div className="flex items-center gap-3.5 pb-5 border-b border-slate-100 dark:border-slate-800">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 text-white flex items-center justify-center shadow-xs">
                    <CreditCard size={20} />
                  </div>
                  <div className="min-w-0">
                    <span className="text-[9px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-widest block">
                      Selected Allocation
                    </span>
                    <h4 className="text-base font-extrabold text-slate-900 dark:text-white truncate mt-0.5">
                      {selectedCard?.name}
                    </h4>
                  </div>
                </div>

                <div className="space-y-3 my-5">
                  <div className="flex justify-between items-center p-3.5 bg-slate-50 dark:bg-[#0B111E] rounded-xl border border-slate-100 dark:border-slate-800/40">
                    <div className="flex items-center gap-2 text-slate-400">
                      <Layers size={14} />
                      <span className="text-xs font-semibold">
                        Total Points
                      </span>
                    </div>
                    <span className="text-sm font-bold text-amber-600 dark:text-amber-400">
                      {Number(
                        selectedCard?.latest_points ||
                          selectedCard?.points ||
                          0,
                      ).toLocaleString()}{" "}
                      pts
                    </span>
                  </div>

                  <div className="flex justify-between items-center p-3.5 bg-slate-50 dark:bg-[#0B111E] rounded-xl border border-slate-100 dark:border-slate-800/40">
                    <div className="flex items-center gap-2 text-slate-400">
                      <DollarSign size={14} />
                      <span className="text-xs font-semibold">
                        Estimated Net Value
                      </span>
                    </div>
                    <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                      ${selectedCard?.latest_max || "0.00"}
                    </span>
                  </div>

                  <div className="flex justify-between items-center p-3.5 bg-slate-50 dark:bg-[#0B111E] rounded-xl border border-slate-100 dark:border-slate-800/40">
                    <div className="flex items-center gap-2 text-slate-400">
                      <TrendingUp size={14} />
                      <span className="text-xs font-semibold">Value Ratio</span>
                    </div>
                    <span className="text-sm font-bold text-slate-700 dark:text-slate-200">
                      {Number(selectedCard?.reward_cpp || 0).toFixed(1)} CPP
                    </span>
                  </div>
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={btnLoading}
                  type="button"
                  className="w-full h-12 
                  rounded-xl 
                  bg-gradient-to-r from-[#CAA036] via-[#B8860B] to-[#9A721C] 
                  hover:from-[#B8860B] hover:to-[#8B6508]
                  text-white font-extrabold text-xs uppercase tracking-wider 
                  flex items-center justify-center gap-2 
                  shadow-[0_4px_14px_rgba(245,158,11,0.3)] 
                  active:scale-[0.97] transition-all duration-150 
                  cursor-pointer touch-manipulation 
                  disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {btnLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Searching Flights...</span>
                    </>
                  ) : (
                    <>
                      <Search size={15} className="stroke-[2.5]" />
                      <span>Use Card & Search</span>
                    </>
                  )}
                </button>
              </>
            ) : (
              <div className="text-center py-6 text-sm text-slate-400">
                Select a card option to view engine routing breakdown details.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Floating Panel Sheet (Mobile Viewport Only Layout) */}
      {selectedCard && (
        <footer className="lg:hidden fixed bottom-0 left-0 right-0 px-4 pt-4 pb-5 bg-white/95 dark:bg-[#151F32]/95 backdrop-blur-md border-t border-slate-200/80 dark:border-slate-800/80 shadow-[0_-8px_24px_rgba(0,0,0,0.06)] rounded-t-3xl z-50 animate-in slide-in-from-bottom duration-200 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
          <div className="max-w-md mx-auto flex flex-col gap-3">
            <div className="flex items-center justify-between px-0.5">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-8 h-8 rounded-xl bg-amber-50 dark:bg-amber-500/10 text-amber-500 flex items-center justify-center shrink-0">
                  <Star size={14} fill="currentColor" />
                </div>
                <div className="min-w-0">
                  <span className="block text-[7px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-widest leading-none">
                    Selected Card Route
                  </span>
                  <h4 className="text-xs font-bold text-slate-900 dark:text-white truncate mt-0.5">
                    {selectedCard?.name}
                  </h4>
                </div>
              </div>

              <div className="flex items-center gap-2 text-right shrink-0 bg-slate-50 dark:bg-[#0B111E] px-2.5 py-1 rounded-xl border border-slate-200/60 dark:border-slate-800/60">
                <div>
                  <span className="block text-[6px] font-bold text-slate-400 uppercase">
                    Net Value
                  </span>
                  <span className="text-[10px] font-extrabold text-emerald-600 dark:text-emerald-400">
                    ${selectedCard?.latest_max || "0.00"}
                  </span>
                </div>
                <div className="w-px h-3 bg-slate-200 dark:bg-slate-700" />
                <div>
                  <span className="block text-[6px] font-bold text-slate-400 uppercase">
                    Ratio
                  </span>
                  <span className="text-[10px] font-extrabold text-slate-700 dark:text-slate-300">
                    {Number(selectedCard?.reward_cpp || 0).toFixed(1)}
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={handleSubmit}
              disabled={btnLoading}
              type="button"
              className="w-full h-12 
              rounded-xl 
              bg-gradient-to-r from-[#CAA036] via-[#B8860B] to-[#9A721C] 
              hover:from-[#B8860B] hover:to-[#8B6508]
              text-white font-extrabold text-xs uppercase tracking-wider 
              flex items-center justify-center gap-2 
              shadow-[0_4px_14px_rgba(245,158,11,0.3)] 
              active:scale-[0.97] transition-all duration-150 
              cursor-pointer touch-manipulation 
              disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {btnLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Searching Flights...</span>
                </>
              ) : (
                <>
                  <Search size={15} className="stroke-[2.5]" />
                  <span>Use Card & Search</span>
                </>
              )}
            </button>

            <div className="flex items-center justify-center gap-1 text-[8px] font-medium text-slate-400 dark:text-slate-500">
              <Lock size={9} />
              <span>Information is secure and fully encrypted</span>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

export default CardSelectionScreen;
